/**
 * RoleWorkflowSummary.jsx — RC EMPLOYER.WORKFLOW.P0 (Founder-approved
 * 2026-02-27).
 *
 * Sits above the dossier list on the Uploaded Dossiers tab and
 * answers the operator's real question: "what factually is the state
 * of this role's dossier corpus?" — using authoritative separate
 * dimensions (Corpus, Admissibility, Evaluation, NDOR Recommendations,
 * Employer Decisions) rather than conflating them into a single
 * "Unevaluated" bucket.
 *
 * Counts are sourced EXCLUSIVELY from the backend `reconciliation`
 * envelope returned by `GET /api/employer/v1/roles/{id}/candidates`.
 * No client-side re-computation. If `reconciliation.invariant_ok` is
 * false, an amber banner strip surfaces exactly which sub-invariant
 * failed.
 *
 * Strict scope:
 *   • Pure presentation over `items` + `reconciliation`.
 *   • Zero new network calls, zero new state machine.
 *   • NDOR counts (Shortlist / Review / Reject) resolve exclusively
 *     from `emp_evaluations.governance_verdict` on the authoritative
 *     row — never from `emp_candidates.governance_status`.
 *   • Employer decisions row surfaces `emp_candidates.governance_status`
 *     tallies computed client-side ONLY across successfully evaluated
 *     candidates — the human governance authority is separate from
 *     the NDOR recommendation authority.
 */
import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Users } from 'lucide-react';


function decisionBucket(items) {
  // Employer decisions counted ONLY over successfully evaluated
  // candidates (evaluation_status === 'complete'). The evaluator
  // never mutates governance_status; every value here is the result
  // of an explicit human PATCH via /candidates/{id}/governance.
  const out = { shortlisted: 0, review: 0, rejected: 0, awaiting: 0 };
  if (!Array.isArray(items)) return out;
  for (const c of items) {
    if (c?.erased_at) continue;
    if (c?.evaluation_status !== 'complete') continue;
    const gs = c.governance_status;
    if (gs === 'shortlisted')     out.shortlisted += 1;
    else if (gs === 'review')     out.review      += 1;
    else if (gs === 'rejected')   out.rejected    += 1;
    else                          out.awaiting    += 1; // 'open' or null
  }
  return out;
}


function CountFragment({ label, count, tone, testId }) {
  const toneMap = {
    ink:     'text-ndor-ink',
    emerald: 'text-emerald-300',
    amber:   'text-amber-300',
    rose:    'text-rose-300',
    slate:   'text-ndor-ink-secondary',
  };
  const cls = toneMap[tone] || toneMap.slate;
  return (
    <span className="text-xs text-ndor-ink-secondary" data-testid={testId}>
      <span className={`font-semibold ${cls}`} data-testid={`${testId}-count`}>{count}</span>
      {' '}{label}
    </span>
  );
}


function Strip({ title, testId, children, extra = null }) {
  return (
    <div
      className="flex items-baseline flex-wrap gap-x-4 gap-y-1"
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-secondary font-medium shrink-0 w-40">
        {title}
      </p>
      <div className="flex items-baseline flex-wrap gap-x-4 gap-y-1">
        {children}
      </div>
      {extra}
    </div>
  );
}


export default function RoleWorkflowSummary({
  items,
  reconciliation = null,
  reviewerActive   = null,
  reviewerPending  = null,
  reviewerInReview = null,
}) {
  const decisions = useMemo(() => decisionBucket(items), [items]);

  // If reconciliation not yet loaded, or the corpus is empty, don't
  // render. The empty-state message already lives inside the parent
  // panel; this component is deliberately silent pre-hydration.
  if (!reconciliation || (reconciliation.n_all ?? 0) === 0) return null;

  const r = reconciliation;

  return (
    <Card
      className="border-ndor-edge bg-ndor-surface"
      data-testid="role-workflow-summary"
    >
      <CardContent className="px-5 py-4 sm:px-6 sm:py-5 space-y-3">
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-secondary font-medium">
            Workflow summary
          </p>
          <p className="text-sm text-ndor-ink-secondary leading-relaxed">
            Progressive workflow: Uploaded → Integrity → Eligible → Assessed →
            NDOR recommendation → Employer decision. Each strip below shows
            the authoritative count for its own dimension.
          </p>
        </div>

        <Strip
          title="Corpus"
          testId="role-workflow-summary-corpus"
        >
          <CountFragment
            label={`candidate${r.n_all === 1 ? '' : 's'} uploaded`}
            count={r.n_all}
            tone="ink"
            testId="role-workflow-corpus-uploaded"
          />
        </Strip>

        <Strip
          title="Admissibility"
          testId="role-workflow-summary-admissibility"
        >
          <CountFragment
            label="awaiting integrity"
            count={r.n_awaiting_integrity}
            tone="amber"
            testId="role-workflow-admissibility-awaiting"
          />
          <CountFragment
            label="excluded"
            count={r.n_excluded}
            tone="rose"
            testId="role-workflow-admissibility-excluded"
          />
          <CountFragment
            label="eligible"
            count={r.n_eligible}
            tone="emerald"
            testId="role-workflow-admissibility-eligible"
          />
        </Strip>

        <Strip
          title="Evaluation"
          testId="role-workflow-summary-evaluation"
        >
          <CountFragment
            label="evaluated"
            count={r.n_completed_auth}
            tone="ink"
            testId="role-workflow-evaluation-completed"
          />
          <CountFragment
            label="eligible awaiting evaluation"
            count={Math.max(0, (r.n_eligible ?? 0) - (r.n_completed_auth ?? 0))}
            tone="slate"
            testId="role-workflow-evaluation-pending"
          />
        </Strip>

        <Strip
          title="NDOR recommendations"
          testId="role-workflow-summary-ndor-recs"
        >
          <CountFragment
            label="shortlist"
            count={r.n_shortlist}
            tone="emerald"
            testId="role-workflow-ndor-shortlist"
          />
          <CountFragment
            label="review"
            count={r.n_review}
            tone="amber"
            testId="role-workflow-ndor-review"
          />
          <CountFragment
            label="reject"
            count={r.n_reject}
            tone="rose"
            testId="role-workflow-ndor-reject"
          />
        </Strip>

        <Strip
          title="Employer decisions"
          testId="role-workflow-summary-employer-decisions"
        >
          <CountFragment
            label="shortlisted"
            count={decisions.shortlisted}
            tone="emerald"
            testId="role-workflow-decision-shortlisted"
          />
          <CountFragment
            label="under review"
            count={decisions.review}
            tone="amber"
            testId="role-workflow-decision-review"
          />
          <CountFragment
            label="rejected"
            count={decisions.rejected}
            tone="rose"
            testId="role-workflow-decision-rejected"
          />
          <CountFragment
            label="awaiting decision"
            count={decisions.awaiting}
            tone="slate"
            testId="role-workflow-decision-awaiting"
          />
        </Strip>

        {/* RC EMPLOYER.WORKFLOW.P0 §Correction 4 — reconciliation
            failures must be visible. When the backend flags
            invariant_ok=false, surface exactly which sub-invariant
            failed so operators can escalate. Never silently coerce a
            broken row into Review or another recommendation. */}
        {r.invariant_ok === false && (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100"
            data-testid="role-workflow-invariant-warning"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-medium">Reconciliation invariant violation.</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-100/90">
                {!r.partition_admissibility_ok && (
                  <li data-testid="role-workflow-invariant-admissibility">
                    Admissibility partition mismatch: {r.n_awaiting_integrity} + {r.n_excluded} + {r.n_eligible} ≠ {r.n_all}.
                  </li>
                )}
                {!r.partition_ndor_ok && (
                  <li data-testid="role-workflow-invariant-ndor-partition">
                    NDOR partition mismatch: {r.n_shortlist} + {r.n_review} + {r.n_reject} + {r.n_unclassified_invalid} ≠ {r.n_completed_auth}.
                  </li>
                )}
                {r.n_unclassified_invalid > 0 && (
                  <li data-testid="role-workflow-invariant-unclassified">
                    {r.n_unclassified_invalid} completed evaluation
                    {r.n_unclassified_invalid === 1 ? '' : 's'} missing a valid
                    recommendation — see backend operator log.
                  </li>
                )}
                {r.n_mirror_authority_mismatch > 0 && (
                  <li data-testid="role-workflow-invariant-mirror">
                    {r.n_mirror_authority_mismatch} candidate
                    {r.n_mirror_authority_mismatch === 1 ? '' : 's'} where the
                    <code className="mx-1 px-1 rounded bg-amber-500/20 font-mono">evaluation_status</code>
                    mirror disagrees with the authoritative
                    <code className="mx-1 px-1 rounded bg-amber-500/20 font-mono">emp_evaluations</code>
                    record. This is a data-integrity signal, not a UI bug.
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {(reviewerActive !== null
          || reviewerPending !== null
          || reviewerInReview !== null) ? (
          <div
            className="flex items-center justify-between gap-3 pt-1 text-xs text-ndor-ink-secondary border-t border-ndor-edge"
            data-testid="role-workflow-reviewer-strip"
          >
            <div className="flex items-center gap-2 pt-2">
              <Users className="h-3.5 w-3.5 opacity-70" />
              <span>
                Reviewer coverage —{' '}
                <Badge
                  variant="outline"
                  className="border-ndor-edge text-ndor-ink-secondary text-[10px] mr-1"
                  data-testid="role-workflow-reviewer-active"
                >
                  {reviewerActive ?? 0} active
                </Badge>
                <Badge
                  variant="outline"
                  className="border-ndor-edge text-ndor-ink-secondary text-[10px] mr-1"
                  data-testid="role-workflow-reviewer-pending"
                >
                  {reviewerPending ?? 0} pending
                </Badge>
                <Badge
                  variant="outline"
                  className="border-ndor-edge text-ndor-ink-secondary text-[10px]"
                  data-testid="role-workflow-reviewer-inreview"
                >
                  {reviewerInReview ?? 0} in review
                </Badge>
              </span>
            </div>
            {(reviewerActive === 0 && decisions.review > 0) ? (
              <span
                className="text-amber-300/90 text-[11px]"
                data-testid="role-workflow-reviewer-warning"
              >
                No reviewers assigned — review verdicts will not progress.
              </span>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
